import ServiceSelector from '@/components/service-selector/ServiceSelector';
import {
  IServiceBlock,
  getServiceBlockTiers,
  useBlocksActions,
  useCellActions,
  useSelectedCell,
} from '@/models/commercial-proposals/blocks.store';
import { useProposal } from '@/models/commercial-proposals/proposal.store';
import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable,
} from '@hello-pangea/dnd';
import {
  Box,
  Button,
  Center,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import BlockBase from './BlockBase';
import ServiceBlockControls from './ServiceBlockControls';
import { IPriceCellConfig } from './cells/ServicePriceCell/ServicePriceCellSettings';
import ServiceHeadingRow from './rows/ServiceHeadingRow/ServiceHeadingRow';
import ServiceMainRow from './rows/ServiceMainRow/ServiceMainRow';
import ServiceSummaryRow from './rows/ServiceSummaryRow/ServiceSummaryRow';

function Settings({ block }: { block: IServiceBlock }) {
  const { update } = useBlocksActions();
  const proposal = useProposal();
  const [opened, { open, close }] = useDisclosure(false);

  if (!block) return null;

  return (
    <Stack>
      <TextInput
        size="xs"
        label="Title"
        value={block.title}
        onChange={(e) => update({ ...block, title: e.target.value })}
      />
      <Box>
        <Switch
          size="xs"
          label="Show heading row"
          checked={block.config.showHeadingRow}
          onChange={(e) =>
            update({
              ...block,
              config: { ...block.config, showHeadingRow: e.target.checked },
            })
          }
        />
      </Box>
      <Box>
        <Switch
          size="xs"
          label="Show summary row"
          checked={block.config.showSummaryRow}
          onChange={(e) =>
            update({
              ...block,
              config: { ...block.config, showSummaryRow: e.target.checked },
            })
          }
        />
      </Box>
      <Box>
        <ServiceSelector
          jurisdiction={proposal!.jurisdiction}
          currency={proposal!.currency}
          opened={opened}
          onClose={close}
          exclude={block.services
            .filter((service) => !service.parentId)
            .map((service) => service.serviceId)}
          onSelect={(service) => {
            update({
              ...block,
              services: [
                ...block.services,
                {
                  blockUuid: block.uuid,
                  serviceId: service.id,
                  price: {
                    amount: service.price,
                    currency: service.currency,
                  },
                  config: {
                    showDescription: false,
                    showChildServices: false,
                    customDescription: service.description || '',
                    ...Array.from({
                      length: getServiceBlockTiers(block.type),
                    }).reduce(
                      (acc: Record<number, IPriceCellConfig>, _, index) => {
                        acc[index] = {
                          included: true,
                          displayIcon: true,
                        };
                        return acc;
                      },
                      {},
                    )!,
                  },
                  weight: block.services.length,
                } as IServiceBlock['services'][number],
              ],
            });
          }}
        />
        <Button size="xs" variant="light" onClick={open}>
          Add Service
        </Button>
      </Box>
    </Stack>
  );
}

export default function ServicesBlock({
  block,
  index,
}: {
  block: IServiceBlock;
  index: number;
}) {
  const services = block.services.filter((s) => !s.parentId);
  const { update } = useBlocksActions();
  const selectedCell = useSelectedCell();
  const { select: selectCell } = useCellActions();
  const propsal = useProposal();

  if (!block || !propsal) return null;

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) return;

    const reorderedServices = [...services];

    const [removed] = reorderedServices.splice(sourceIndex, 1);
    reorderedServices.splice(destinationIndex, 0, removed);

    update({
      ...block,
      services: reorderedServices
        .flatMap((service) => {
          return [
            service,
            ...block.services.filter((s) => s.parentId === service.serviceId),
          ];
        })
        .map((service, idx) => ({ ...service, weight: idx })),
    });

    if (selectedCell) {
      selectCell(null);
    }
  };

  return (
    <BlockBase
      block={block}
      index={index}
      settings={<Settings block={block} />}
      rightActions={<ServiceBlockControls block={block} />}
    >
      {!services.length ? (
        <Center>
          <Text size="xs" mb="md" c="dimmed">
            No services added yet
          </Text>
        </Center>
      ) : (
        <>
          <Stack>
            {block.config.showHeadingRow && <ServiceHeadingRow block={block} />}
          </Stack>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId={`services-${block.uuid}`}>
              {(provided) => (
                <Stack
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  gap={0}
                >
                  {services
                    .filter((s) => !s.parentId)
                    .map((blockService, index) => (
                      <Draggable
                        key={blockService.serviceId}
                        draggableId={String(blockService.serviceId)}
                        index={index}
                      >
                        {(draggableProvided) => (
                          <ServiceMainRow
                            service={blockService}
                            provided={draggableProvided}
                            block={block}
                            index={index}
                          />
                        )}
                      </Draggable>
                    ))}
                  {provided.placeholder}
                </Stack>
              )}
            </Droppable>
          </DragDropContext>
          {block.config.showSummaryRow && (
            <ServiceSummaryRow block={block} currency={propsal!.currency} />
          )}
        </>
      )}
    </BlockBase>
  );
}
